<template>
	<div class="stage">
		<page-header title="Accounts" />

		<filter-bar
			:show-filters="showFilters"
			:has-export="true"
			:has-view-toggle="!isSmallScreen"
			:view-mode="effectiveViewMode"
			:show-add-button="$global.hasUserRole(userRoles, 'accounts:create')"
			add-button-label="Add Account"
			@toggle-filters="showFilters = !showFilters"
			@toggle-view="handleViewModeChange"
			@reset-filters="resetSearchParams"
			@export="exportToCsv"
			@add-new="routeTo('/accounts/new')"
		/>

		<filter-section
			:show="showFilters"
			:filters="searchParams"
			:search-term="searchField"
			:tenants="tenants"
			date-label="Date Added"
			show-tenant-filter
			show-date-filter
			show-search-filter
			show-status-filter
			search-label="Name"
			@update:filters="searchParams = $event"
			@update:search-term="searchField = $event"
			@tenant-change="$global.setSelectedTenant"
		/>

		<data-view
			:view-mode="viewMode"
			:columns="columns"
			:sort-column="sortColumn"
			:reverse="reverse"
			:loading="loading"
			:label="'Accounts'"
			:total-items="totalAccounts"
			:page-size="pageSize"
			:current-page="currentPage"
			:pages="pages"
			@sort="setSort"
			@update:page-size="changePageSize"
			@page-change="changePage"
		>
			<template #table-view>
				<generic-table
					:items="accounts"
					:columns="columns"
					item-type="accounts"
					base-route="accounts"
					:can-edit="$global.hasUserRole(userRoles, 'accounts:update')"
					:view-mode="'table'"
					:empty-state-config="{
						icon: 'fa-light fa-user',
						title: 'No accounts yet',
						description: 'Create your first account to start managing users and billing.',
						action: $global.hasUserRole(userRoles, 'accounts:create')
							? {
									label: 'Create Account',
									icon: 'fa-light fa-plus',
									handler: () => routeTo('/accounts/new')
								}
							: null
					}"
					:actions="[
						{
							handler: (account) => routeTo(`/accounts/${account.id}`),
							icon: 'fa-light fa-pencil',
							label: 'Edit'
						}
					]"
				>
					<template #name="{ item }">
						<span
							v-if="
								item?.status !== 'active' ||
								(item?.balanceGbp <= 0 && item?.billingType === 'PRE_PAID') ||
								!item?.aLegTariffId ||
								!item?.bLegTariffProfileId
							"
						>
							<span
								class="fa-light fa-triangle-exclamation mr-2 text-red-500"
								@mouseover="
									tooltip(
										$event,
										[
											item?.status !== 'active' &&
												'Account: ' +
													(item?.status
														? item.status.charAt(0).toUpperCase() + item.status.slice(1)
														: ''),
											item?.balanceGbp <= 0 &&
												item?.billingType === 'PRE_PAID' &&
												'Balance: ' + $global.currencyGbp(item?.balanceGbp, 2),
											!item?.aLegTariffId && 'No A-Leg Tariff',
											!item?.bLegTariffProfileId && 'No B-Leg Tariff Profile'
										]
											.filter(Boolean)
											.join('<br/>')
									)
								"
							></span>
							{{ item?.name }}
						</span>
					</template>
					<template #tenant="{ item }">
						{{ item.tenant?.companyName || 'Unassigned' }}
					</template>
					<template #authType="{ item }">
						{{ $global.authType(item.authType) }}
					</template>
					<template #status="{ item }">
						<span class="capitalize">{{ item.status }}</span>
					</template>
					<template #mobile-view="{ item }">
						<div class="flex w-full flex-col">
							<div class="text-md absolute right-4 top-4 block max-w-[160px]">
								<p class="text-right font-semibold uppercase text-gray-400">Company name</p>
								<p class="truncate text-right">{{ item.tenant?.companyName || 'Unassigned' }}</p>
							</div>

							<div class="flex flex-col gap-2">
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Account name:</p>
									<p>{{ item.name }}</p>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Status:</p>
									<p class="capitalize">{{ item.status }}</p>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Created:</p>
									<p>{{ $global.dateFormat(item.createdAt) }}</p>
								</div>
							</div>
						</div>
					</template>
				</generic-table>
			</template>

			<template #card-view>
				<router-link
					v-for="account in accounts"
					:key="account.id"
					:to="`/accounts/${account.id}`"
					class="noLink flex cursor-default flex-col gap-4 rounded-lg bg-white p-5 shadow-md hover:bg-[var(--color-white-dark)] active:bg-[var(--color-white-dark-hover)] dark:bg-[var(--color-offmaindark)] dark:shadow-lg dark:hover:bg-[var(--color-offmaindark-hover)] dark:active:bg-[var(--color-offmaindark-active)]"
				>
					<div class="flex flex-row justify-between">
						<div
							class="flex flex-col justify-center pl-3"
							:style="{
								'border-left': `4px solid ${
									account.status === 'active'
										? 'green'
										: account.status === 'inactive'
											? '#edc42d'
											: 'red'
								}`
							}"
						>
							<span class="font-semi-bold text-lg text-black dark:text-white">{{ account?.name }}</span>
							<p class="text-lg" :class="{ 'text-red-500': account.balanceGbp <= 0 }">
								{{ $global.currencyGbp(account.balanceGbp, 2) }}
							</p>
						</div>
						<div class="flex flex-col">
							<div class="flex items-center gap-4">
								<div class="flex w-4 items-center justify-center">
									<span class="fa-light fa-lock text-blue-800 dark:text-blue-300"></span>
								</div>
								<p class="truncate text-black dark:text-white">
									{{
										account.authType === 'SOURCE_IP'
											? 'Source IP'
											: account.authType === 'SIP_HEADER'
												? 'SIP Header'
												: 'Inbound Only'
									}}
								</p>
							</div>
							<div class="flex items-center gap-4">
								<div class="flex w-4 items-center justify-center">
									<span class="fa-light fa-id-card text-blue-800 dark:text-blue-300"></span>
								</div>
								<p class="text-black dark:text-white">
									{{ account.prefix ? account.prefix : 'No Prefix' }}
								</p>
							</div>
							<div class="flex items-center gap-4 text-blue-800 dark:text-blue-300">
								<div class="flex w-4 items-center justify-center">
									<span class="fa-light fa-credit-card text-blue-800 dark:text-blue-300"></span>
								</div>
								<p class="text-black dark:text-white">
									{{ account.billingType === 'PRE_PAID' ? 'Prepaid' : 'Postpaid' }}
								</p>
							</div>
						</div>
					</div>
					<div class="grid w-full grid-cols-2 gap-2">
						<div class="max-w-1/2 flex flex-col gap-0.5">
							<p class="text-xs font-semibold uppercase text-gray-500">A-leg Tariff</p>
							<div class="flex items-center justify-start gap-2">
								<a
									:href="account.aLegTariffId ? `/tariffs/${account.aLegTariffId}` : null"
									class="group flex w-full items-center gap-2 truncate"
									@click.stop
								>
									<div class="min-w-0 truncate">
										<p
											class="truncate text-black dark:text-white"
											:class="{ 'group-hover:underline': account.aLegTariffId }"
										>
											{{ lookupTariffName(account.aLegTariffId) }}
										</p>
									</div>
									<span
										v-if="account.aLegTariffId"
										class="fa-solid fa-link fa-xs text-blue-800 dark:text-blue-300"
									></span>
								</a>
							</div>
						</div>
						<div class="max-w-1/2 flex flex-col gap-0.5 truncate">
							<p class="text-xs font-semibold uppercase text-gray-500">B-leg Routing Profile</p>
							<div class="flex items-center justify-start gap-2">
								<a
									:href="
										account.bLegTariffProfileId
											? `/tariffprofiles/${account.bLegTariffProfileId}`
											: null
									"
									class="group flex w-full items-center gap-2 truncate"
									@click.stop
								>
									<div class="min-w-0 truncate">
										<p
											class="truncate text-black dark:text-white"
											:class="{ 'group-hover:underline': account.bLegTariffProfileId }"
										>
											{{ lookupTariffProfileName(account.bLegTariffProfileId) }}
										</p>
									</div>
									<span
										v-if="account.bLegTariffProfileId"
										class="fa-solid fa-link fa-xs shrink-0 text-blue-800 dark:text-blue-300"
									></span>
								</a>
							</div>
						</div>
					</div>
					<div class="flex items-end justify-between gap-4">
						<div class="flex flex-col">
							<p class="text-xs font-semibold uppercase text-gray-500">Tenant</p>
							<p>{{ account.tenant.companyName }}</p>
						</div>
						<div v-if="$global.hasUserRole(userRoles, 'realtime:getall')" class="mt-1 flex items-center">
							<span class="fa-solid fa-phone mr-2 text-blue-800 dark:text-blue-300"></span>
							<span
								class="mr-4 font-bold"
								:class="[
									(callsByAccount?.[account.id]?.length || 0) > 0
										? 'text-green-500 dark:text-[#a0ce7f]'
										: 'text-black dark:text-white'
								]"
							>
								{{ callsByAccount?.[account.id]?.length || 0 }}</span
							>
							<span class="fa-solid fa-lock mr-2 text-blue-800 dark:text-blue-300"></span>
							<span
								class="mr-4 font-bold"
								:class="[
									(registrationsByAccount?.[account.id]?.length || 0) > 0
										? 'text-green-500 dark:text-[#a0ce7f]'
										: 'text-black dark:text-white'
								]"
							>
								{{ registrationsByAccount?.[account.id]?.length || 0 }}</span
							>
							<span class="fa-solid fa-sim-card mr-2 text-blue-800 dark:text-blue-300"></span>
							<span
								class="mr-4 font-bold"
								:class="[
									(simsByAccount?.[account.id]?.length || 0) > 0
										? 'text-green-500 dark:text-[#a0ce7f]'
										: 'text-black dark:text-white'
								]"
							>
								{{ simsByAccount?.[account.id]?.length || 0 }}</span
							>
						</div>
					</div>
				</router-link>
			</template>
		</data-view>
	</div>
</template>

<script>
import accountsApi from '../scripts/accounts/accounts.api'
import tariffsApi from '@/scripts/tariffs/tariffs.api'
import tariffprofilesApi from '@/scripts/tariffprofiles/tariffprofiles.api'
import { router } from '../router'
import { ref } from 'vue'
import moment from 'moment'
import axios from 'axios'
import debounce from 'lodash.debounce'
import realtimeApi from '@/scripts/realtime/realtime.api'

export default {
	inject: ['user', 'userRoles', 'tenants'],

	beforeRouteLeave() {
		this.stopPolling = true
		clearInterval(this.getRefreshInterval)
	},

	setup() {
		const date = ref(new Date())
		const dp = ref()

		return {
			date,
			dp,
			selectDate: () => dp.value.selectDate(),
			closeMenu: () => dp.value.closeMenu()
		}
	},
	data() {
		return {
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-3'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'authType',
					label: 'Auth Type',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-1'
				},
				{
					key: 'prefix',
					label: 'Prefix',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-1 col-lg-2'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 hideTabletLandscape'
				},
				{
					key: 'status',
					label: 'Status',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				}
			],
			showFilters: true,
			error: '',
			dates: [],
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				status: null
			},
			accounts: [],
			totalAccounts: 0,
			allSelected: false,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'name',
			reverse: false,
			loading: false,
			searchField: '',
			viewMode: localStorage.getItem('accountsViewMode') || 'card',
			hover: null,
			pills: [],
			getRefreshInterval: null,
			tariffNames: [],
			tariffProfileNames: [],
			callsByAccount: {},
			registrationsByAccount: {},
			simsByAccount: {},
			stopPolling: false,
			debouncedWatch: null,
			isSmallScreen: window.innerWidth < 768
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalAccounts / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},

		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === this.searchParams.selectedTenant)
			return tenant?.companyName || 'All Tenants'
		},

		effectiveViewMode() {
			if (this.isSmallScreen) {
				return 'card'
			}
			return this.viewMode
		}
	},

	watch: {
		searchParams: {
			handler() {
				this.reloadAccounts()
			},
			deep: true
		},

		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},

		dates: {
			handler(val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					this.reloadAccounts()
				}
			},
			deep: true
		},

		currentPage() {
			this.reloadAccounts()
		},

		'$route.query.globalSearch'(newValue) {
			if (newValue) this.reloadAccounts(newValue)
		},

		viewMode(newMode) {
			if (newMode === 'card') {
				this.startRefreshInterval()
			} else {
				this.stopRefreshInterval()
			}
		}
	},

	async created() {
		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
		}

		window.addEventListener('resize', this.handleResize)
		this.handleResize()

		this.debouncedWatch = debounce(this.reloadAccounts, 1000)

		if (this.$route.query?.globalSearch) {
			this.reloadAccounts(this.$route.query.globalSearch)
		} else {
			this.reloadAccounts()
		}

		this.tariffNames = await tariffsApi.getAllNames()
		this.tariffProfileNames = await tariffprofilesApi.getAllNames()

		this.reloadData()

		this.startRefreshInterval()
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
		this.stopPolling = true
		clearInterval(this.getRefreshInterval)
		this.stopRefreshInterval()
		window.removeEventListener('resize', this.handleResize)
	},

	mounted() {
		window.addEventListener('resize', this.handleResize)
		this.handleResize()
	},

	methods: {
		async reloadData() {
			try {
				if (!this.$global.hasUserRole(this.userRoles, 'realtime:getall') || !realtimeApi) return
				const { data } = await realtimeApi.getAll()
				if (!data) return

				const { callLegs, registrations, sims } = data

				this.registrationsByAccount =
					registrations?.reduce((acc, reg) => {
						const accountId = reg?.sipTrunk?.accountId
						if (accountId) {
							acc[accountId] = acc[accountId] || []
							acc[accountId].push(reg)
						}
						return acc
					}, {}) || {}

				this.callsByAccount =
					callLegs?.reduce((acc, call) => {
						if (call?.teleflowId && call?.accountId) {
							acc[call.accountId] = acc[call.accountId] || []
							if (!acc[call.accountId].includes(call.teleflowId)) {
								acc[call.accountId].push(call.teleflowId)
							}
						}
						return acc
					}, {}) || {}

				this.simsByAccount =
					sims?.reduce((acc, reg) => {
						const accountId = reg?.mobileSim?.accountId
						if (accountId) {
							acc[accountId] = acc[accountId] || []
							acc[accountId].push(reg)
						}
						return acc
					}, {}) || {}

				if (!this.stopPolling) {
					setTimeout(this.reloadData, 2000)
				}
			} catch (error) {
				console.error(error)
				if (!this.stopPolling) {
					setTimeout(this.reloadData, 5000)
				}
			}
		},

		resetDates() {
			this.dates = []
			this.searchParams.dateAddedFrom = null
			this.searchParams.dateAddedTo = null
		},

		formatDates(date1, date2) {
			if (!date1) return null
			const format = 'Do MMM YY'
			return date2
				? `${moment(date1).format(format)} to ${moment(date2).format(format)}`
				: moment(date1).format(format)
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/accounts`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = 'teleflow-accounts.csv'
				link.click()
			} catch (error) {
				console.error(error)
			}
		},

		setSort(column, reverse) {
			this.reverse = this.sortColumn === column ? reverse : false
			this.sortColumn = column
			this.reloadAccounts()
		},

		changePage(page) {
			this.currentPage = page
			this.reloadAccounts()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalAccounts / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.reloadAccounts()
		},

		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn || 'createdAt']: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams) {
				const { dateAddedFrom, dateAddedTo, status, selectedTenant } = this.searchParams

				if (dateAddedFrom) {
					params.where.createdAtFrom = `${moment(dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
					params.where.createdAtTo = dateAddedTo
						? `${moment(dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
						: `${moment(dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
				}

				if (status) params.where.status = status
				if (selectedTenant) params.where.selectedTenant = selectedTenant
			}

			return params
		},

		async reloadAccounts(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await accountsApi.getAll(params)
				this.totalAccounts = response.data.count
				this.accounts = response.data.results

				const maxPages = Math.ceil(this.totalAccounts / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadAccounts(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		clearSelectedTenant() {
			this.searchParams.selectedTenant = ''
			this.$global.setSelectedTenant({ target: { value: '' } })
		},

		startRefreshInterval() {
			if (!this.getRefreshInterval && this.$global.hasUserRole(this.userRoles, 'realtime:getall')) {
				this.getRefreshInterval = setInterval(() => {
					this.fetchRealtimeData()
				}, 3000)
				this.fetchRealtimeData()
			}
		},

		stopRefreshInterval() {
			if (this.getRefreshInterval) {
				clearInterval(this.getRefreshInterval)
				this.getRefreshInterval = null
			}
		},

		handleViewModeChange(newMode) {
			if (this.isSmallScreen) {
				this.viewMode = 'card'
			} else {
				this.viewMode = newMode
				localStorage.setItem('accountsViewMode', newMode)
			}
			if (this.viewMode === 'card') {
				this.startRefreshInterval()
			} else {
				this.stopRefreshInterval()
			}
		},

		async fetchRealtimeData() {
			try {
				if (!this.$global.hasUserRole(this.userRoles, 'realtime:getall')) return
				const { data } = await realtimeApi.getAll()
				const { callLegs, registrations, sims } = data

				this.registrationsByAccount =
					registrations?.reduce((acc, reg) => {
						const accountId = reg?.sipTrunk?.accountId
						if (accountId) {
							acc[accountId] = acc[accountId] || []
							acc[accountId].push(reg)
						}
						return acc
					}, {}) || {}

				this.callsByAccount =
					callLegs?.reduce((acc, call) => {
						if (call?.teleflowId && call?.accountId) {
							acc[call.accountId] = acc[call.accountId] || []
							if (!acc[call.accountId].includes(call.teleflowId)) {
								acc[call.accountId].push(call.teleflowId)
							}
						}
						return acc
					}, {}) || {}

				this.simsByAccount =
					sims?.reduce((acc, reg) => {
						const accountId = reg?.mobileSim?.accountId
						if (accountId) {
							acc[accountId] = acc[accountId] || []
							acc[accountId].push(reg)
						}
						return acc
					}, {}) || {}
			} catch (error) {
				console.error('Error fetching realtime data:', error)
			}
		},

		lookupTariffName(id) {
			if (!this.tariffNames?.length) return '(not set)'
			return this.tariffNames.find((t) => t.entity_id === parseInt(id))?.entity_name || '(not set)'
		},

		lookupTariffProfileName(id) {
			if (!this.tariffProfileNames?.length) return '(not set)'
			return this.tariffProfileNames.find((t) => t.entity_id === parseInt(id))?.entity_name || '(not set)'
		},

		handleResize() {
			this.isSmallScreen = window.innerWidth < 768
			if (this.isSmallScreen && this.viewMode === 'table') {
				this.handleViewModeChange('card')
			} else if (!this.isSmallScreen) {
				const savedMode = localStorage.getItem('accountsViewMode')
				this.handleViewModeChange(savedMode || 'table')
			}
		}
	}
}
</script>
